import { useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import cn from 'classnames';

import { ReactComponent as DownloadIcon } from 'assets/download.svg';
import dropDownIcon from 'assets/dropDownIcon.svg';
import noReviews from 'assets/noReviews.png';
import noReviewsFound from 'assets/noReviewsFound.png';
import ReviewsMagnifyIcon from 'assets/ReviewsMagnifyIcon.svg';
import ReviewsSearchTimer from 'assets/ReviewsSearchTimer.svg';

import { getDesignAuditCountsAction, getDesignAuditHistoryAction } from 'Redux/Actions/designAuditActions';

import { DESIGN_AUDIT_STATUS, PAGE_SIZE, PDF_STATUS } from 'utlis/constants';
import { downloadReviewPdf } from 'utlis/pdfUtils';
import { formatDate } from 'utlis/string_format';

import StatusBadge from './StatusBadge';

// Time filter constants
const TIME_FILTERS = {
  PAST_24_HOURS: 'Past 24 hours',
  LAST_7_DAYS: 'Last 7 days',
  LAST_14_DAYS: 'Last 14 days',
  LAST_30_DAYS: 'Last 30 days',
  ALL_TIME: 'All Time',
};

const ReviewsSection = ({
  isCreateReviewOpen,
  statusFilter,
  onClearStatusFilter,
  onViewReview,
  onCreateReviewToggle,
}) => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState(TIME_FILTERS.LAST_30_DAYS);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingItems, setDownloadingItems] = useState(new Set());
  const pollingIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const designAuditCounts = useSelector(state => state.designAudit.designAuditCounts);
  const designAuditHistory = useSelector(state => state.designAudit.designAuditHistory);
  const tokenRefreshTimestamp = useSelector(state => state.userStore.tokenRefreshTimestamp);

  // Map time filter to days
  const getDaysFromFilter = filter => {
    switch (filter) {
      case TIME_FILTERS.PAST_24_HOURS:
        return 1;
      case TIME_FILTERS.LAST_7_DAYS:
        return 7;
      case TIME_FILTERS.LAST_14_DAYS:
        return 14;
      case TIME_FILTERS.LAST_30_DAYS:
        return 30;
      case TIME_FILTERS.ALL_TIME:
        return 365;
      default:
        return 30;
    }
  };

  // Check if a review needs polling (processing and created within last hour, or completed without PDF)
  const needsPolling = review => {
    // Processing reviews within last hour
    if (review.status === DESIGN_AUDIT_STATUS.IN_PROGRESS) {
      const createdAt = new Date(review.created_at);
      const now = new Date();
      const hourInMs = 60 * 60 * 1000;

      return (now - createdAt) < hourInMs;
    }

    // Completed reviews without PDF link
    if (review.status === 2) {
      const pdfProcess = review.post_processes?.find(val => val.process_type === 1);

      return pdfProcess && pdfProcess.status !== PDF_STATUS.READY && pdfProcess.status !== PDF_STATUS.FAILED;
    }

    return false;
  };

  // Get PDF process from review
  const getPdfProcess = review => review.post_processes?.find(val => val.process_type === 1);

  // Check if PDF is ready for download
  const isPdfReady = review => {
    const pdfProcess = getPdfProcess(review);

    return pdfProcess && pdfProcess.status === PDF_STATUS.READY && pdfProcess.result_data?.pdf_link;
  };

  // Check if any reviews need polling
  const hasReviewsNeedingPolling = useMemo(
    () => designAuditHistory?.results?.some(review => needsPolling(review)) || false,
    [designAuditHistory?.results]
  );

  // Fetch design audit history
  const fetchDesignAuditHistory = async (
    page = 1,
    days = 30,
    updateLoading = true,
    search = null,
    reviewStatus = null
  ) => {
    if (updateLoading) {
      setIsLoading(true);
    }

    try {
      await dispatch(getDesignAuditHistoryAction(days, page, PAGE_SIZE, search, reviewStatus));
    }
    finally {
      // Ensure loading state is reset after fetching
      if (updateLoading) {
        setIsLoading(false);
      }
    }
  };

  // Handle search with debouncing
  const handleSearchChange = e => {
    const newSearchTerm = e.target.value;

    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for API call
    searchTimeoutRef.current = setTimeout(() => {
      const days = getDaysFromFilter(timeFilter);

      fetchDesignAuditHistory(1, days, true, newSearchTerm, statusFilter);
    }, 500); // 500ms debounce
  };

  useEffect(() => {
    const days = getDaysFromFilter(timeFilter);

    fetchDesignAuditHistory(currentPage, days, true, searchTerm, statusFilter);
  }, [dispatch, currentPage, timeFilter, statusFilter]);

  // Effect to handle status filter changes
  useEffect(() => {
    if (statusFilter !== null) {
      setCurrentPage(1); // Reset to first page when filtering
      const days = getDaysFromFilter(timeFilter);

      fetchDesignAuditHistory(1, days, true, searchTerm, statusFilter);
    }
  }, [statusFilter]);

  // Polling effect for processing reviews
  useEffect(() => {
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        // Skip API call if create review popup is open
        if (!isCreateReviewOpen) {
          const days = getDaysFromFilter(timeFilter);

          fetchDesignAuditHistory(currentPage, days, false, searchTerm, statusFilter);
          dispatch(getDesignAuditCountsAction());
        }
      }, 30000); // Poll every 30 seconds
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (hasReviewsNeedingPolling) {
      startPolling();
    }
    else {
      stopPolling();
    }

    return stopPolling;
  }, [hasReviewsNeedingPolling, currentPage, timeFilter, isCreateReviewOpen, searchTerm, statusFilter]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Listen for token refresh events and refetch design audit history
  useEffect(() => {
    if (tokenRefreshTimestamp) {
      const days = getDaysFromFilter(timeFilter);

      fetchDesignAuditHistory(currentPage, days);
    }
  }, [tokenRefreshTimestamp, currentPage, timeFilter]);

  // Handle time filter change
  const handleTimeFilterChange = newFilter => {
    setTimeFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle page change
  const handlePageChange = page => {
    setCurrentPage(page);
  };

  // Handle PDF download
  const handleDownloadReport = async review => {
    const pdfProcess = getPdfProcess(review);

    if (!pdfProcess?.result_data?.pdf_link) {
      return;
    }

    setDownloadingItems(prev => new Set(prev).add(review.uuid));

    try {
      await downloadReviewPdf(review, pdfProcess.result_data.pdf_link);
    }
    finally {
      setDownloadingItems(prev => {
        const newSet = new Set(prev);

        newSet.delete(review.uuid);

        return newSet;
      });
    }
  };

  // Get status filter display name
  const getStatusFilterDisplay = () => {
    switch (statusFilter) {
      case DESIGN_AUDIT_STATUS.IN_PROGRESS:
        return 'Processing';
      case DESIGN_AUDIT_STATUS.COMPLETED:
        return 'Completed';
      default:
        return null;
    }
  };

  // Calculate pagination info
  const totalPages = Math.ceil(designAuditHistory.count / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, designAuditHistory.count);

  const hasNoReviews = useMemo(() => designAuditCounts.reviews_total === 0, [designAuditCounts]);

  // Generate page numbers for pagination
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }
    else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');

      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else {
      pages.push(1);
      pages.push('...');

      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-12">
          <h2 className="font-inter text-lg font-semibold text-figma-gray-900 leading-6">
            My reviews
          </h2>
          {statusFilter && (
            <div
              className="bg-gray-200 flex items-center px-3 py-1 gap-4 text-primary-700 rounded-full text-sm"
            >
              <span className="text-primary-700 rounded-full text-xs">
                {getStatusFilterDisplay()}
              </span>
              <button
                onClick={onClearStatusFilter}
                className="rounded-full"
                title="Clear filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm font-inter
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-140"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <img src={ReviewsMagnifyIcon} width={20} height={20} alt="Search" />
            </div>
          </div>

          {/* Time Filter */}
          <div className="flex items-center gap-8 bg-white border border-gray-300 rounded-md px-3 py-2 w-44">
            <img src={ReviewsSearchTimer} alt="Time Filter" width={20} height={20} />
            <select
              value={timeFilter}
              onChange={e => handleTimeFilterChange(e.target.value)}
              className="appearance-none bg-transparent text-sm font-inter text-gray-700 focus:outline-none"
            >
              <option>
                {TIME_FILTERS.PAST_24_HOURS}
              </option>
              <option>
                {TIME_FILTERS.LAST_7_DAYS}
              </option>
              <option>
                {TIME_FILTERS.LAST_14_DAYS}
              </option>
              <option>
                {TIME_FILTERS.LAST_30_DAYS}
              </option>
              <option>
                {TIME_FILTERS.ALL_TIME}
              </option>
            </select>
            <img src={dropDownIcon} alt="Dropdown" className="ml-auto" />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        {isLoading
          ? (
            <div className="bg-white rounded-lg overflow-hidden flex-1 flex justify-center items-center">
              <div className="flex justify-center items-center flex-1">
                <ImpulseSpinner frontColor={'#28BDAE'} />
              </div>
            </div>
          )
          : (
            designAuditHistory.results.length === 0
              ? (
                <div className="flex flex-1 flex-col justify-center items-center py-16 overflow-auto gap-14 2xl:gap-20">
                  <img src={hasNoReviews ? noReviews : noReviewsFound} className="w-36	2xl:w-52 h-auto" />
                  <div className="flex flex-col items-center gap-8 2xl:gap-12 text-center">
                    <span className="font-inter text-lg text-gray-900 font-semibold">
                      {hasNoReviews ? 'You don\'t have any reviews yet' : 'No reviews found'}
                    </span>
                    <span className="font-md text-gray-900">
                      {hasNoReviews
                        ? 'Create your first review now'
                        : 'There are no reviews currently available for the selected filters.'}
                    </span>
                  </div>
                  {hasNoReviews && (
                    <button
                      onClick={handleNewReview}
                      className="flex items-center justify-center px-3 py-2 bg-primary-500 text-white font-inter
                      text-sm font-medium rounded-lg shadow-shadow-xs hover:bg-primary-600 transition-colors"
                    >
                      New review
                    </button>
                  )}
                </div>
              )
              : (
                <div className="bg-white rounded-lg flex flex-col flex-1 overflow-auto">
                  {/* Table Header */}
                  <div className="flex items-center gap-4 px-16 py-12 border-b border-gray-200">
                    <div className="flex-[4] flex items-center">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5 overflow-hidden">
                        Review
                      </span>
                    </div>
                    <div className="flex-[2] flex">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Status
                      </span>
                    </div>
                    <div className="flex-[2] flex items-center">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Last updated
                      </span>
                    </div>
                    <div className="flex-[1] flex justify-start">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Action
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1 overflow-auto">
                    {
                      designAuditHistory.results.map(review => {
                        const hasFailed = review.status === DESIGN_AUDIT_STATUS.FAILURE;

                        return (
                          <div
                            role="button"
                            key={review.uuid}
                            className={cn('group flex items-center gap-4 px-16 py-12 border-b border-gray-10', {
                              'cursor-not-allowed opacity-60': hasFailed,
                              'hover:bg-gray-50 cursor-pointer': !hasFailed,
                            })}
                            onClick={() => !hasFailed && onViewReview(review)}
                          >
                            <div className="flex-[4] flex items-center">
                              <div
                                className="font-inter text-sm font-medium text-figma-gray-900 leading-5 overflow-hidden"
                              >
                                {review.title}
                              </div>
                            </div>
                            <div className="flex-[2] flex">
                              <StatusBadge status={review.status} />
                            </div>
                            <div className="flex-[2] flex items-center">
                              <span className="font-inter text-sm font-medium text-figma-gray-600 leading-5">
                                {formatDate(review.updated_at)}
                              </span>
                            </div>
                            <div className="flex-[1] flex justify-start">
                              {isPdfReady(review) && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleDownloadReport(review);
                                  }}
                                  disabled={downloadingItems.has(review.uuid)}
                                  title="Download PDF report"
                                >
                                  <Tooltip
                                    title={
                                      downloadingItems.has(review.uuid)
                                        ? 'Preparing download...'
                                        : 'Download PDF report'
                                    }
                                    placement="left"
                                  >
                                    <DownloadIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                                  </Tooltip>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>

                  {/* Pagination */}
                  {designAuditHistory.count > 0 && (
                    <div className="flex items-center justify-between px-16 py-12 border-t border-gray-200 mt-auto">
                      <div className="font-inter text-sm text-figma-gray-600">
                        Showing
                        {' '}
                        <span className="font-semibold">
                          {startItem}
                          -
                          {endItem}
                        </span>
                        {' '}
                        of
                        {' '}
                        <span className="font-semibold">
                          {designAuditHistory.count}
                        </span>
                      </div>
                      <div className="flex items-center border border-[#CBCFD6] rounded-md">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-figma-gray-600
                      hover:text-figma-gray-900 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {pageNumbers.map((page, index) => (
                          <button
                            key={index}
                            onClick={() => typeof page === 'number' && handlePageChange(page)}
                            disabled={page === '...'}
                            className={`flex px-3 py-[0.375rem] flex-col justify-center items-center text-sm font-medium ${
                              page === currentPage
                                ? 'bg-primary-400 text-white'
                                : page === '...'
                                  ? 'bg-white text-figma-gray-400 cursor-default'
                                  : 'bg-white text-figma-gray-600 hover:text-figma-gray-900'
                            }`}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium
                      text-figma-gray-600 hover:text-figma-gray-900 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
          )}
      </div>
    </>
  );

  function handleNewReview() {
    onCreateReviewToggle(true);
  }
};

ReviewsSection.propTypes = {
  isCreateReviewOpen: PropTypes.bool.isRequired,
  statusFilter: PropTypes.number,
  onClearStatusFilter: PropTypes.func.isRequired,
  onViewReview: PropTypes.func.isRequired,
  onCreateReviewToggle: PropTypes.func.isRequired,
};

export default ReviewsSection;
